import { HowItWorks } from 'components/common/HowItWorks'
import { useOverlay } from 'contexts'
import { useRouter } from 'next/router'
import { useCallback, useEffect } from 'react'
import { TradeFlowType } from 'types'

interface Config {
  /**
   * Will not run on mount if `show_hiw=false` is present in the search params.
   *
   * `show_hiw=false` is set when redirecting the user after displaying the modal.
   */
  runOnMount?: TradeFlowType
}

export function useHowItWorksModal({ runOnMount }: Config = {}) {
  const { setConfig, setIsOpen } = useOverlay()
  const router = useRouter()

  const showAgainHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const {
        currentTarget: { id, checked },
      } = e

      if (id === 'dont-show-again-buy') {
        if (checked) {
          localStorage.setItem('hideBuy', String(checked))
          return
        }

        localStorage.removeItem('hideBuy')
        return
      }

      if (id === 'dont-show-again-sell') {
        if (checked) {
          localStorage.setItem('hideSell', String(checked))
          return
        }

        localStorage.removeItem('hideSell')
        return
      }
    },
    []
  )

  const parseHref = (href?: string) => {
    if (!href) return undefined

    const additionalParam = 'show_hiw=false'

    if (href.includes('?')) {
      return href + '&' + additionalParam
    }

    return href + '?' + additionalParam
  }

  const maybeShow = useCallback(
    (type: TradeFlowType, href?: string) => {
      const isSell = type === 'ask-or-sell'
      const hide = localStorage.getItem(isSell ? 'hideSell' : 'hideBuy')
      const _href = parseHref(href)

      if (!hide) {
        setConfig({
          className: 'lg:flex sm:items-center',
          body: (
            <HowItWorks
              showAgainHandler={showAgainHandler}
              href={_href}
              isSell={isSell}
            />
          ),
          scrollOnClose: true,
        })
        setIsOpen(true)
        return true
      }

      return false
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showAgainHandler]
  )

  useEffect(() => {
    if (runOnMount && router.query.show_hiw !== 'false') {
      maybeShow(runOnMount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { maybeShow }
}
